import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  useUploadFile,
  useSubmitAnswers,
  useFinalize,
  TOLKNING_FIELDS,
  FELT_LABELS,
  type DokumentTolkning,
  type KonfidensVerdi,
  type MissingQuestion,
} from '../hooks/useUpload';
import { useRole, hasMinimumRole } from '../hooks/useRole';
import { apiGet } from '../services/api';

type StepKey = 'upload' | 'review' | 'missing' | 'link' | 'confirm' | 'receipt';
const STEPS: StepKey[] = ['upload', 'review', 'missing', 'link', 'confirm', 'receipt'];
const STEP_LABELS = ['Last opp', 'Gjennomgang', 'Utfyll mangler', 'Koble til linje', 'Bekreft', 'Kvittering'];

function konfidensColor(k: number): 'success' | 'warning' | 'error' {
  if (k >= 0.8) return 'success';
  if (k >= 0.5) return 'warning';
  return 'error';
}

// --- Step 1: File Upload ---
function FileDropZone({ onFileSelected, isUploading, error }: {
  onFileSelected: (file: File) => void; isUploading: boolean; error: string | null;
}) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileSelected(file);
  }, [onFileSelected]);

  return (
    <Box>
      <Paper
        variant="outlined"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          p: 6, textAlign: 'center', border: 2, borderStyle: 'dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          bgcolor: dragOver ? 'primary.50' : 'background.default',
          transition: 'all 0.2s',
        }}
      >
        {isUploading ? (
          <Box>
            <CircularProgress size={32} sx={{ mb: 1 }} />
            <Typography variant="body2">Laster opp og analyserer...</Typography>
            <Typography variant="caption" color="text.secondary">Dette kan ta noen sekunder</Typography>
          </Box>
        ) : (
          <>
            <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Dra og slipp en fil her, eller
            </Typography>
            <Button variant="contained" component="label">
              Velg fil
              <input type="file" hidden accept=".pdf,.docx,.txt" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFileSelected(file);
              }} />
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              PDF, DOCX eller TXT (maks 10 MB)
            </Typography>
          </>
        )}
      </Paper>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
}

// --- Step 2: Review ---
function TolkningReview({ tolkning, onEdit, onNext, onBack }: {
  tolkning: DokumentTolkning;
  onEdit: (field: string, value: string | number | null) => void;
  onNext: () => void; onBack: () => void;
}) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (field: string) => {
    const kv = tolkning[field] as KonfidensVerdi | undefined;
    setEditingField(field);
    setEditValue(kv?.verdi != null ? String(kv.verdi) : '');
  };

  const saveEdit = () => {
    if (editingField) { onEdit(editingField, editValue || null); setEditingField(null); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Gjennomgå AI-tolkningen. Klikk på et felt for å redigere.
      </Typography>
      <Paper variant="outlined">
        {TOLKNING_FIELDS.map((field) => {
          const kv = tolkning[field] as KonfidensVerdi | undefined;
          if (!kv) return null;
          const isEditing = editingField === field;
          return (
            <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ width: 180, flexShrink: 0, fontWeight: 500 }}>
                {FELT_LABELS[field] || field}
              </Typography>
              <Box sx={{ flex: 1 }}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField size="small" value={editValue} onChange={(e) => setEditValue(e.target.value)}
                      autoFocus onKeyDown={(e) => e.key === 'Enter' && saveEdit()} sx={{ flex: 1 }} />
                    <Button size="small" variant="contained" onClick={saveEdit}>Lagre</Button>
                    <Button size="small" onClick={() => setEditingField(null)}>Avbryt</Button>
                  </Box>
                ) : (
                  <Typography variant="body2" onClick={() => startEdit(field)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, px: 1, py: 0.5, borderRadius: 1 }}>
                    {kv.verdi != null ? String(kv.verdi) : <em style={{ color: '#999' }}>Ikke funnet</em>}
                  </Typography>
                )}
              </Box>
              <Chip label={`${Math.round(kv.konfidens * 100)}%`} size="small" color={konfidensColor(kv.konfidens)} />
            </Box>
          );
        })}
      </Paper>
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}>Tilbake</Button>
        <Button variant="contained" onClick={onNext}>Neste</Button>
      </Box>
    </Box>
  );
}

// --- Step 3: Missing Fields ---
function MissingFieldsForm({ missingQuestions, onSubmitStructured, onSubmitFreeText, isSubmitting, onBack, onSkip }: {
  missingQuestions: MissingQuestion[];
  onSubmitStructured: (answers: Record<string, string>) => void;
  onSubmitFreeText: (text: string) => void;
  isSubmitting: boolean; onBack: () => void; onSkip: () => void;
}) {
  const [mode, setMode] = useState<'structured' | 'freetext'>('structured');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [freeText, setFreeText] = useState('');

  if (missingQuestions.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Alle felt har høy nok konfidens. Du kan gå videre.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={onBack}>Tilbake</Button>
          <Button variant="contained" onClick={onSkip}>Neste</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ToggleButtonGroup value={mode} exclusive onChange={(_e, v) => v && setMode(v)} size="small" sx={{ mb: 2 }}>
        <ToggleButton value="structured">Felt for felt</ToggleButton>
        <ToggleButton value="freetext">Fritekst</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'structured' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {missingQuestions.map((q) => (
            <TextField key={q.felt} label={FELT_LABELS[q.felt] || q.felt} helperText={q.sporsmal}
              value={answers[q.felt] || ''} onChange={(e) => setAnswers((p) => ({ ...p, [q.felt]: e.target.value }))}
              size="small" fullWidth />
          ))}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={onBack}>Tilbake</Button>
            <Button variant="contained" onClick={() => onSubmitStructured(answers)} disabled={isSubmitting}>
              {isSubmitting ? 'Sender...' : 'Oppdater'}
            </Button>
            <Button onClick={onSkip}>Hopp over</Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Skriv svarene dine i fritekst. AI vil tolke og knytte svarene til riktige felt.
          </Typography>
          <TextField multiline rows={5} value={freeText} onChange={(e) => setFreeText(e.target.value)}
            placeholder="F.eks: Tittelen er Husleiekontrakt, betaler er Firma AS..." fullWidth />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={onBack}>Tilbake</Button>
            <Button variant="contained" onClick={() => onSubmitFreeText(freeText)} disabled={isSubmitting || !freeText.trim()}>
              {isSubmitting ? 'Tolker...' : 'Send'}
            </Button>
            <Button onClick={onSkip}>Hopp over</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// --- Step 4: Income Line Selector ---
interface IncomeLine {
  InntektslinjeID: string; Aar: number; Enhet: string; Ansvar: string; Konto: string; KontoNavn: string;
}

function InntektslinjeSelector({ onSelect, onSkip, onBack }: {
  onSelect: (id: string) => void; onSkip: () => void; onBack: () => void;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<IncomeLine[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const doSearch = async () => {
    if (!search.trim()) return;
    setIsSearching(true);
    try {
      const resp = await apiGet<{ data: IncomeLine[] }>(`/income-lines?search=${encodeURIComponent(search)}&pageSize=20`);
      setResults(resp.data);
    } catch { setResults([]); }
    setIsSearching(false);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Søk etter en inntektslinje for å koble avtalen. Du kan også hoppe over dette steget.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          placeholder="Søk på ansvar, konto, kontonavn..." sx={{ flex: 1 }} />
        <Button variant="contained" onClick={doSearch} disabled={isSearching}>
          {isSearching ? '...' : 'Søk'}
        </Button>
      </Box>

      {results.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, maxHeight: 280, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>ID</TableCell>
                <TableCell>Ansvar</TableCell>
                <TableCell>Konto</TableCell>
                <TableCell>KontoNavn</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((line) => (
                <TableRow key={line.InntektslinjeID} hover selected={selected === line.InntektslinjeID}
                  onClick={() => setSelected(line.InntektslinjeID)} sx={{ cursor: 'pointer' }}>
                  <TableCell padding="checkbox">
                    <Radio checked={selected === line.InntektslinjeID} size="small" />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{line.InntektslinjeID}</TableCell>
                  <TableCell>{line.Ansvar}</TableCell>
                  <TableCell>{line.Konto}</TableCell>
                  <TableCell>{line.KontoNavn}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}>Tilbake</Button>
        {selected && <Button variant="contained" onClick={() => onSelect(selected)}>Velg og gå videre</Button>}
        <Button onClick={onSkip}>Hopp over</Button>
      </Box>
    </Box>
  );
}

// --- Step 5: Confirm ---
function FinalizeConfirm({ tolkning, inntektslinjeID, filename, onConfirm, onBack, isSubmitting }: {
  tolkning: DokumentTolkning; inntektslinjeID: string | null; filename: string;
  onConfirm: () => void; onBack: () => void; isSubmitting: boolean;
}) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Kontroller oppsummeringen og bekreft.</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}><strong>Fil:</strong> {filename}</Typography>
        {inntektslinjeID && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Koblet til:</strong> <code>{inntektslinjeID}</code>
          </Typography>
        )}
        {TOLKNING_FIELDS.map((field) => {
          const kv = tolkning[field] as KonfidensVerdi | undefined;
          if (!kv) return null;
          return (
            <Box key={field} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">{FELT_LABELS[field] || field}</Typography>
              <Typography variant="body2" fontWeight={500}>{kv.verdi != null ? String(kv.verdi) : '-'}</Typography>
            </Box>
          );
        })}
      </Paper>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}>Tilbake</Button>
        <Button variant="contained" color="success" onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting ? 'Lagrer...' : 'Bekreft og lagre'}
        </Button>
      </Box>
    </Box>
  );
}

// --- Step 6: Receipt ---
function UploadReceipt({ avtaleId, dokumentId, finalFilename }: {
  avtaleId: string; dokumentId: string; finalFilename: string;
}) {
  const navigate = useNavigate();
  return (
    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Opplasting fullført</Typography>
      <Typography variant="body2">Avtale opprettet med ID: <code>{avtaleId}</code></Typography>
      <Typography variant="body2">Dokument-ID: <code>{dokumentId}</code></Typography>
      <Typography variant="body2">Filnavn: {finalFilename}</Typography>
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button variant="contained" size="small" onClick={() => navigate(`/agreements/${avtaleId}`)}>
          Gå til avtalen
        </Button>
        <Button variant="outlined" size="small" onClick={() => navigate(0)}>
          Last opp ny fil
        </Button>
      </Box>
    </Alert>
  );
}

// --- Main Page ---
export function UploadPage() {
  const role = useRole();
  const [step, setStep] = useState<StepKey>('upload');
  const [dokumentId, setDokumentId] = useState<string | null>(null);
  const [filename, setFilename] = useState('');
  const [tolkning, setTolkning] = useState<DokumentTolkning | null>(null);
  const [inntektslinjeID, setInntektslinjeID] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<{ avtaleId: string; dokumentId: string; finalFilename: string } | null>(null);

  const uploadMutation = useUploadFile();
  const answersMutation = useSubmitAnswers();
  const finalizeMutation = useFinalize();

  if (role === null) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!hasMinimumRole(role, 'Editor')) {
    return <Alert severity="warning">Du trenger minst Editor-rolle for å laste opp dokumenter.</Alert>;
  }

  const currentStepIndex = STEPS.indexOf(step);

  const handleFileSelected = (file: File) => {
    uploadMutation.mutate(file, {
      onSuccess: (resp) => {
        setDokumentId(resp.data.dokumentId); setFilename(resp.data.filename);
        setTolkning(resp.data.tolkning); setStep('review');
      },
    });
  };

  const handleEditField = (field: string, value: string | number | null) => {
    if (!tolkning) return;
    setTolkning({
      ...tolkning,
      [field]: { verdi: value, konfidens: 1.0, source_quote: 'Manuelt redigert' },
      missing_questions: tolkning.missing_questions.filter((q) => q.felt !== field),
    });
  };

  const handleStructuredAnswers = (answers: Record<string, string>) => {
    if (!dokumentId || !tolkning) return;
    const nonEmpty = Object.fromEntries(Object.entries(answers).filter(([, v]) => v.trim() !== ''));
    if (Object.keys(nonEmpty).length === 0) { setStep('link'); return; }
    answersMutation.mutate({ dokumentId, tolkning, answers: nonEmpty }, {
      onSuccess: (resp) => { setTolkning(resp.data.tolkning); setStep('link'); },
    });
  };

  const handleFreeText = (text: string) => {
    if (!dokumentId || !tolkning) return;
    answersMutation.mutate({ dokumentId, tolkning, freeText: text, missingQuestions: tolkning.missing_questions }, {
      onSuccess: (resp) => { setTolkning(resp.data.tolkning); setStep('link'); },
    });
  };

  const handleFinalize = () => {
    if (!dokumentId || !tolkning) return;
    finalizeMutation.mutate({ dokumentId, tolkning, inntektslinjeID: inntektslinjeID || undefined }, {
      onSuccess: (resp) => { setReceipt(resp.data); setStep('receipt'); },
    });
  };

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>Last opp inntektsgrunnlag</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Last opp dokumentasjon som underbygger en inntektslinje — for eksempel:
      </Typography>
      <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
        {[
          'Kontrakt eller leieavtale (husleie, festeavgift, bruksrett)',
          'Kommunalt vedtak (gebyr, egenandel, tilskudd)',
          'Interkommunal avtale eller samarbeidsavtale',
          'Statlig tilskuddsbrev eller tildelingsvedtak',
          'Faktura eller budsjettnotat som dokumenterer forventet inntekt',
        ].map((t) => (
          <Typography key={t} component="li" variant="body2" color="text.secondary">{t}</Typography>
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        AI leser dokumentet, trekker ut nøkkelinformasjon og foreslår kobling til riktig inntektslinje.
        Støttede formater: PDF, DOCX, TXT (maks 10 MB).
      </Typography>

      <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: 4 }}>
        {STEP_LABELS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {step === 'upload' && (
        <FileDropZone onFileSelected={handleFileSelected} isUploading={uploadMutation.isPending}
          error={uploadMutation.error ? (uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Opplasting feilet') : null} />
      )}
      {step === 'review' && tolkning && (
        <TolkningReview tolkning={tolkning} onEdit={handleEditField}
          onNext={() => setStep(tolkning.missing_questions.length > 0 ? 'missing' : 'link')}
          onBack={() => setStep('upload')} />
      )}
      {step === 'missing' && tolkning && (
        <MissingFieldsForm missingQuestions={tolkning.missing_questions}
          onSubmitStructured={handleStructuredAnswers} onSubmitFreeText={handleFreeText}
          isSubmitting={answersMutation.isPending}
          onBack={() => setStep('review')} onSkip={() => setStep('link')} />
      )}
      {step === 'link' && (
        <InntektslinjeSelector
          onSelect={(id) => { setInntektslinjeID(id); setStep('confirm'); }}
          onSkip={() => { setInntektslinjeID(null); setStep('confirm'); }}
          onBack={() => setStep(tolkning?.missing_questions.length ? 'missing' : 'review')} />
      )}
      {step === 'confirm' && tolkning && (
        <FinalizeConfirm tolkning={tolkning} inntektslinjeID={inntektslinjeID} filename={filename}
          onConfirm={handleFinalize} onBack={() => setStep('link')} isSubmitting={finalizeMutation.isPending} />
      )}
      {step === 'receipt' && receipt && (
        <UploadReceipt avtaleId={receipt.avtaleId} dokumentId={receipt.dokumentId} finalFilename={receipt.finalFilename} />
      )}
    </Box>
  );
}
